import { Injectable } from '@angular/core';
import { DateRangePickerValue } from '../date-range-picker/date-range-picker.component';
import { AvailabilityModel } from 'src/app/providers/availability.provider';
import { API_DAY_FORMAT } from 'src/app/shared/date-utils';
import { PricingSchemas } from 'src/app/models/enums';
import { BasketItemOption, OptionQuantity } from 'src/app/models/product-order.model';
import { ProductOptionModel } from 'src/app/models/product-option.model';
import { PricingScheme } from 'src/app/models/pricing-schema.model';

@Injectable()
export class ProductItemService {
  hotelPriceCalculations(
    hotelBookingPeriod: DateRangePickerValue,
    availabilities: Record<string, AvailabilityModel>,
    numberOfRooms: number,
    pricingSchemas: PricingScheme[],
    selectedAvailability: AvailabilityModel
  ): HotelPriceCalculations {
    const availabilityForPeriod = this.hotelAvailabilityForPeriod(hotelBookingPeriod, availabilities);

    const numberOfNights = availabilityForPeriod.length;

    const adultPricingScheme = pricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Adult);
    const childPricingScheme = pricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.Child && x.count > 0);
    const extraBedsPricingScheme = pricingSchemas.find((x) => x.pricingSchemaId === PricingSchemas.ExtraBed);

    const roomChildPricings = {
      pricingSchemaId: childPricingScheme?.pricingSchemaId,
      rate: childPricingScheme ? this.pricingSchemaRate(childPricingScheme, selectedAvailability) : 0,
      lastRoomRate: childPricingScheme ? this.pricingSchemaRate(childPricingScheme, selectedAvailability, true) : 0,
      count: childPricingScheme?.count,
    };

    const filledChildRoom = childPricingScheme
      ? Math.floor((childPricingScheme.count - 1) / childPricingScheme.maximumCapacity)
      : 0;

    const roomAdultPricings = {
      pricingSchemaId: adultPricingScheme.pricingSchemaId,
      rate: this.pricingSchemaRate(adultPricingScheme, selectedAvailability),
      lastRoomRate: this.pricingSchemaRate(adultPricingScheme, selectedAvailability, true),
      count: adultPricingScheme.count,
    };

    let firstAdultRoomsTotal = 0;
    let firstChildRoomsTotal = 0;
    if (numberOfRooms > 1) {
      firstAdultRoomsTotal = roomAdultPricings.rate * numberOfNights * (numberOfRooms - 1);
    }

    if (filledChildRoom >= 1) {
      firstChildRoomsTotal = roomChildPricings.rate * numberOfNights * filledChildRoom;
    }

    const lastAdultRoomTotal = roomAdultPricings.lastRoomRate * numberOfNights;

    let lastChildRoomTotal = 0;
    if (childPricingScheme) {
      lastChildRoomTotal = roomChildPricings.lastRoomRate * numberOfNights;
    }

    let total = firstAdultRoomsTotal + lastAdultRoomTotal + firstChildRoomsTotal + lastChildRoomTotal;

    if (extraBedsPricingScheme?.count > 0) {
      total = total + extraBedsPricingScheme.count * extraBedsPricingScheme.rate * numberOfNights;
    }

    let roomsSummary: HotelRoom[] = [];
    if (numberOfRooms > 1) {
      for (let i = 0; i < numberOfRooms - 1; i++) {
        const firstRooms = {
          adults: adultPricingScheme.maximumCapacity,
          roomTotal: roomAdultPricings.rate,
        };

        roomsSummary.push(firstRooms);
      }
    }

    const lastRoom = {
      adults: this.getLastRoomPricingSchemeCount(adultPricingScheme),
      roomTotal: roomAdultPricings.lastRoomRate,
    };

    roomsSummary.push(lastRoom);

    if (childPricingScheme) {
      if (filledChildRoom >= 1) {
        for (let i = 0; i < filledChildRoom; i++) {
          roomsSummary[i].children = childPricingScheme.maximumCapacity;
          roomsSummary[i].roomTotal += roomChildPricings.rate;
        }
      }

      roomsSummary[filledChildRoom].children = this.getLastRoomPricingSchemeCount(childPricingScheme);
      roomsSummary[filledChildRoom].roomTotal += roomChildPricings.lastRoomRate;
    }

    return {
      extraBedsTotal:
        extraBedsPricingScheme?.count > 0
          ? extraBedsPricingScheme?.count * extraBedsPricingScheme.rate * numberOfNights
          : 0,
      numberOfNights,
      roomsSummary,
      total,
    };
  }

  getLastRoomPricingSchemeCount(scheme: PricingScheme) {
    return scheme.count - scheme.maximumCapacity * this.getNumberOfFilledUpRooms(scheme);
  }

  getNumberOfFilledUpRooms(scheme: PricingScheme) {
    return Math.ceil(scheme.count / scheme.maximumCapacity) - 1;
  }

  pricingSchemaRate(scheme: PricingScheme, selectedAvailability: AvailabilityModel, lastRoom = false): number {
    const numberOfFilledUpRooms = Math.ceil(scheme.count / scheme.maximumCapacity) - 1;

    const schemeCount = scheme.count - scheme.maximumCapacity * numberOfFilledUpRooms;

    // If `lastRoom` is false, it means that all the rooms except the last one
    // are full and we want to find the correct pricing scheme rate

    // If `lastRoom` is true, it means that we want to find the correct pricing scheme rate
    // based on the selected count for the last room
    return selectedAvailability.pricingSchemas.find(
      (s) =>
        this.isInRange(lastRoom ? schemeCount : scheme.maximumCapacity, [s.minimumCapacity, s.maximumCapacity]) &&
        s.name === scheme.name
    )?.rate;
  }

  private isInRange(n: number, [begin, end]: [number, number]): boolean {
    [begin, end] = end > begin ? [begin, end] : [end, begin];

    return n >= begin && n <= end;
  }

  getLowestPriceForPeriod(
    hotelBookingPeriod: DateRangePickerValue,
    availabilities: Record<string, AvailabilityModel>
  ): number {
    const availabilityRates = this.hotelAvailabilityForPeriod(hotelBookingPeriod, availabilities).map((a) => {
      const pricingSchemaRates = a.pricingSchemas
        .filter((s) => s.pricingSchemaId === PricingSchemas.Adult)
        .map((s) => s.rate);

      return Math.min(...pricingSchemaRates);
    });

    return Math.min(...availabilityRates);
  }

  hotelAvailabilityForPeriod(
    hotelBookingPeriod: DateRangePickerValue,
    availabilities: Record<string, AvailabilityModel>
  ): AvailabilityModel[] {
    const startDay = hotelBookingPeriod.start.format(API_DAY_FORMAT);
    const endDay = hotelBookingPeriod.end.format(API_DAY_FORMAT);

    // Because for hotels we count nights, we’re not including the last day, the checkout day.
    return Object.values(availabilities).filter((a) => a.day >= startDay && a.day < endDay);
  }

  getMinRoomQuantityForPeriod(
    hotelBookingPeriod: DateRangePickerValue,
    availabilities: Record<string, AvailabilityModel>
  ): number {
    const roomCountsForPeriod = this.hotelAvailabilityForPeriod(hotelBookingPeriod, availabilities).map(
      (a) => a.availableCapacity
    );

    return Math.min(...roomCountsForPeriod);
  }

  // TODO: Add unit test.
  buildCartItem(
    hotelBookingPeriod: DateRangePickerValue,
    option: ProductOptionModel,
    availabilities: Record<string, AvailabilityModel>,
    selectedRoomQuantity: number,
    selectedAvailability: AvailabilityModel
  ): BasketItemOption {
    const totalPrice = this.hotelPriceCalculations(
      hotelBookingPeriod,
      availabilities,
      selectedRoomQuantity,
      option.pricingSchemas,
      selectedAvailability
    ).total;

    const itemQuantities: OptionQuantity[] = option.pricingSchemas
      .filter((x) => x.count > 0)
      .map((x) => {
        return {
          quantity: x.count,
          pricingSchemaId: x.pricingSchemaId,
          price: x.rate,
          name: x.name,
        };
      });

    const { start, end } = hotelBookingPeriod;

    return {
      optionId: option.id,
      name: option.name,
      from: start.format(API_DAY_FORMAT),
      to: end.format(API_DAY_FORMAT),
      optionQuantities: itemQuantities,
      roomQuantity: selectedRoomQuantity,
      productTypeId: option.product.productTypeId,
      totalPrice,
    };
  }
}

export interface HotelPriceCalculations {
  extraBedsTotal?: number;
  numberOfNights: number;
  roomsSummary: HotelRoom[];
  total: number;
}

interface HotelRoom {
  adults: number;
  children?: number;
  roomTotal: number;
}
